/** Small shared UI bits for the Face Search admin pages (self-contained, Tailwind only). */
import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";

export const ACCENT = "#f5a623";

export const STATUS = {
  listing: { label: "Reading folder", cls: "bg-sky-500/15 text-sky-300" },
  listed: { label: "Ready to index", cls: "bg-zinc-500/20 text-zinc-200" },
  indexing: { label: "Indexing", cls: "bg-amber-500/15 text-amber-300" },
  paused: { label: "Paused", cls: "bg-orange-500/15 text-orange-300" },
  ready: { label: "Live", cls: "bg-emerald-500/15 text-emerald-300" },
  error: { label: "Error", cls: "bg-red-500/15 text-red-300" },
};

export function StatusChip({ status, active = true }) {
  const s = STATUS[status] || { label: status, cls: "bg-zinc-500/20 text-zinc-200" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${active ? s.cls : "bg-zinc-700/40 text-zinc-400"}`}>
      {status === "indexing" && active && <Loader2 className="h-3 w-3 animate-spin" />}
      {active ? s.label : "Inactive"}
    </span>
  );
}

export function BrandChip({ brand }) {
  const label = brand === "triobliss" ? "Trio Bliss" : "Wittyshadows";
  return <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs text-white/70">{label}</span>;
}

export function ProgressBar({ value, className = "" }) {
  return (
    <div className={`h-2 w-full overflow-hidden rounded-full bg-white/10 ${className}`}>
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, value || 0))}%`, background: ACCENT }} />
    </div>
  );
}

export function Btn({ children, className = "", variant = "primary", loading = false, ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:opacity-50 disabled:pointer-events-none";
  const styles = {
    primary: "text-black hover:brightness-110",
    ghost: "border border-white/10 bg-white/5 text-white hover:bg-white/10",
    danger: "border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20",
    link: "text-amber-300 hover:underline px-1",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} style={variant === "primary" ? { background: ACCENT } : undefined} disabled={loading || rest.disabled} {...rest}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {children}
    </button>
  );
}

export function Modal({ open, title, onClose, children, width = "max-w-lg" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className={`w-full ${width} max-h-[92vh] overflow-y-auto rounded-t-2xl border border-white/10 bg-[#141416] p-5 text-white shadow-2xl sm:rounded-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-white/60 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const inputCls = "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-amber-400/60";

export function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium uppercase tracking-wider text-white/50">{label}</span>
      {children}
      {hint && <span className="block text-xs text-white/40">{hint}</span>}
    </label>
  );
}

export function Toggle({ checked, onChange, label, hint }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex w-full items-center justify-between gap-4 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-left">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-white/50">{hint}</span>}
      </span>
      <span className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? "" : "bg-white/15"}`} style={checked ? { background: ACCENT } : undefined}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}

export function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value ?? "—"}</div>
      {sub && <div className="text-xs text-white/50">{sub}</div>}
    </div>
  );
}

export function useToastState() {
  const [toast, setToast] = useState(null);
  const show = (msg, kind = "info") => {
    setToast({ msg, kind, id: Date.now() });
    setTimeout(() => setToast((t) => (t && Date.now() - t.id >= 3000 ? null : t)), 3200);
  };
  const node = toast ? (
    <div className={`fixed left-1/2 top-4 z-[100] -translate-x-1/2 rounded-full px-4 py-2 text-sm text-white shadow-xl ${toast.kind === "error" ? "bg-red-500/95" : toast.kind === "success" ? "bg-emerald-500/95" : "bg-zinc-800/95"}`}>
      {toast.msg}
    </div>
  ) : null;
  return [node, show];
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
export const usd = (n) => `$${(n || 0).toFixed(2)}`;
